import { ChangeDetectionStrategy, Component, effect, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { DateTimeService } from '@examdojo/core/date-time';
import { ListViewSearchFieldComponent } from '@examdojo/core/list-view';
import { ColDef, ColGroupDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { UserAnalyticsAgGridService, UserAnalyticsData } from './user-analytics-ag-grid.service';

@Component({
  selector: 'exam-user-analytics-ag-grid',
  imports: [ExamdojoAgGridModule, ListViewSearchFieldComponent, MatSlideToggle, ReactiveFormsModule],
  templateUrl: './user-analytics-ag-grid.component.html',
  styleUrls: ['./user-analytics-ag-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserAnalyticsAgGridService],
  host: { class: 'flex h-full w-full flex-col' },
})
export class UserAnalyticsAgGridComponent implements OnInit {
  constructor(
    private readonly agGridService: UserAnalyticsAgGridService,
    private readonly dateTimeService: DateTimeService,
  ) {
    // Set up search functionality
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.gridApi()?.setGridOption('quickFilterText', searchText ?? '');
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Set up past exams toggle functionality
    this.includePastExamsCtrl.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap((includePastExams) => {
          this.applyPastExamsFilter(includePastExams ?? false);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Update grid when data or columns change
    effect(() => {
      const analytics = this.state.analytics;
      if (analytics && this.gridApi()) {
        // Update column definitions if they've changed
        if (analytics.columnDefs) {
          this.gridApi()!.setGridOption('columnDefs', analytics.columnDefs);
        }

        // Size columns to fit if we have data
        if (analytics.data && analytics.data.length > 0) {
          this.sizeColumnsToFit();
        }
      }
    });
  }

  readonly gridApi = signal<GridApi<UserAnalyticsData> | null>(null);
  readonly searchCtrl = new FormControl<string | null>('');
  readonly includePastExamsCtrl = new FormControl<boolean>(false); // Default: exclude past exams

  readonly state = connectState({
    analytics: this.agGridService.getData(),
    tableMetadata: this.agGridService.fetchTableMetadata(),
  }) as {
    analytics: {
      data: UserAnalyticsData[];
      columnDefs: Array<ColDef<UserAnalyticsData> | ColGroupDef<UserAnalyticsData>>;
      pinnedBottomRowData: UserAnalyticsData[];
      isLoading: boolean;
    } | null;
    tableMetadata: {
      lastModified: Date;
    } | null;
  };

  get gridOptions() {
    return this.agGridService.gridOptions;
  }

  get columnDefs() {
    // Return dynamic columns from state if available, otherwise fall back to static columns
    return this.state.analytics?.columnDefs ?? this.agGridService.columnDefs;
  }

  get lastModifiedText(): string {
    const metadata = this.state.tableMetadata;
    if (!metadata?.lastModified || isNaN(metadata.lastModified.getTime())) {
      return '';
    }

    const timeAgo = this.dateTimeService.formatDateRelativeToNowShort(metadata.lastModified);
    return `last-modified: ${timeAgo}`;
  }

  ngOnInit(): void {
    // Data loading is handled by connectState automatically
    // No manual subscription needed
  }

  onGridReady(event: GridReadyEvent<UserAnalyticsData>): void {
    this.gridApi.set(event.api);

    // Set dynamic columns and totals row if data is already available
    const analytics = this.state.analytics;
    if (analytics) {
      // Set column definitions if available
      if (analytics.columnDefs) {
        event.api.setGridOption('columnDefs', analytics.columnDefs);
      }

      // Size columns to fit if we have data
      if (analytics.data && analytics.data.length > 0) {
        this.sizeColumnsToFit();
      }
    }

    // Apply initial filter based on toggle state
    setTimeout(() => {
      this.applyPastExamsFilter(this.includePastExamsCtrl.value ?? false);
    }, 100);
  }

  private applyPastExamsFilter(includePastExams: boolean): void {
    const api = this.gridApi();
    if (!api) {
      return;
    }

    const filterModel = api.getFilterModel();

    if (includePastExams) {
      // Show all rows - remove the is_past_exam filter
      if (filterModel['is_past_exam']) {
        delete filterModel['is_past_exam'];
      }
    } else {
      // Hide past exams - filter out rows where is_past_exam = true
      filterModel['is_past_exam'] = {
        filterType: 'set',
        values: ['false'], // Only show rows where is_past_exam = false
      };
    }

    api.setFilterModel(filterModel);
  }

  private sizeColumnsToFit(): void {
    // Use a small delay to ensure the grid has finished rendering
    setTimeout(() => {
      this.gridApi()?.sizeColumnsToFit();
    }, 50);
  }
}
